/**
 * keep.js — «Сохранить предмет»: an object the person taps stays in the cut.
 *
 * WHY. RobustVideoMatting is a HUMAN matting network: whatever is not a person
 * is background to it. Measured 23.09.2026 on real pianist footage (Wikimedia
 * Commons, CC BY-SA): on a wide stage shot it kept the grand piano's rim and cut
 * the open lid and the legs to shreds; on a close-up it removed the piano
 * outright. Support popova_katerina asked for exactly this — a performer AND
 * her instrument on a new background. Retraining the matting net is not the
 * answer; a promptable segmenter is: the person taps the object, EdgeTAM cuts
 * it, the tracker carries it through the clip, and the pipeline unions that
 * silhouette with the person's matte.
 *
 * ONE ENGINE, NOT TWO. Everything heavy is the worker /remove-object-from-video
 * already runs (remove-object/ro-worker.js): EdgeTAM on onnxruntime-web behind
 * the known-answer provider ladder, the tap selector, the silhouette tracker.
 * The mask at an arbitrary time is RtMaskBuilder.rasterize — the same function
 * that tool paints its removals with, asked here for a TIGHT silhouette
 * (`grow`) instead of a removal's generous one: a removal must swallow the
 * object, a keep must not bring a halo of the old room with it.
 *
 * Public API — window.VbrKeep:
 *   configure(cfg)                        {workerUrl, engineUrls, seg}
 *   available()      → bool               model files + ORT owner present
 *   warm()           → Promise<{ok}>      start the model download early
 *   sample(opts, onProgress) → Promise    decode the samples the tracker reads
 *                                         opts: {file,url,trimStart,trimEnd,videoW,videoH,abortSignal}
 *   tap(nx, ny, tSec) → Promise<{ok, reason, mask, w, h}>   select / refine
 *   track(onProgress) → Promise<{ok, kfs, masks, lost}>
 *   active()         → bool               a tracked object will be kept
 *   maskAt(tSec, w, h) → Uint8Array|null  0/255 silhouette at the working size
 *   mergeInto(matte, frame, tSec) → bool  union the object into a matte canvas
 *   previewMask()    → {mask,w,h}|null    the selection being built (grid size)
 *   reset()                               forget the object (worker stays warm)
 */
(function (root) {
    'use strict';

    var CFG = null;
    var W = null, pend = new Map(), nextId = 0;
    var seg = { promise: null, state: 'idle', provider: null, adapter: null };
    var verdictKey = null;
    var K = null;                // the clip's samples + the object on it
    var trackProgress = null;

    /* Samples the tracker reads. The long side is the grid the segmenter sees;
       the sampling rate falls with length so a 60-second clip stays inside a
       phone's memory (480×270×4 = 518 KB a sample; 60 of them ≈ 31 MB). */
    var GRID_LONG = 480, MAX_SAMPLES = 60, MAX_FPS = 4, MIN_FPS = 1;
    /* A keep must not bring the old room with it: no slack beyond one pixel
       (the removal tool grows 7 %). Measured: 1.2 % left an orange ring of stage
       floor round the lid of a grand piano (23.09.2026). */
    var KEEP_GROW = 0;   // → a 1-px ring; the edge is placed by refineEdge, not by slack

    function now() { return (root.performance && performance.now) ? performance.now() : Date.now(); }

    function configure(cfg) { CFG = cfg || null; }
    function available() {
        return !!(CFG && CFG.seg && CFG.workerUrl && root.__ortBuild && typeof root.__ortBuild.url === 'function'
            && root.VbrFrameSource && typeof Worker !== 'undefined');
    }

    function wcall(msg, transfer) {
        return new Promise(function (resolve, reject) {
            msg.id = ++nextId;
            pend.set(msg.id, { resolve: resolve, reject: reject });
            try { W.postMessage(msg, transfer || []); } catch (e) { pend.delete(msg.id); reject(e); }
        });
    }
    function ensureWorker() {
        if (W) return Promise.resolve();
        W = new Worker(CFG.workerUrl);
        W.onmessage = function (ev) {
            var m = ev.data;
            if (m.type === 'trackProgress') { if (trackProgress) trackProgress(m.p); return; }
            if (m.type === 'segVerdict') { onVerdict(m.verdict); return; }
            // The crop lane serves the tool's own box prompts; we only ever tap,
            // but an unanswered request would stall the worker for its timeout.
            if (m.type === 'cropRequest') { try { W.postMessage({ type: 'cropReply', cid: m.cid }); } catch (_) {} return; }
            var p = pend.get(m.id);
            if (!p) return;
            pend.delete(m.id);
            if (m.type === 'error') p.reject(new Error(m.message)); else p.resolve(m);
        };
        W.onerror = function (e) {
            pend.forEach(function (p) { p.reject(new Error((e && e.message) || 'keep worker error')); });
            pend.clear();
        };
        return wcall({ type: 'init', urls: CFG.engineUrls });
    }

    /* Only a WRONG ANSWER is remembered (30 days, this adapter + model) — the
       same memory /remove-object-from-video keeps, under the same key shape, so
       a GPU caught lying on one page is not trusted on the other. */
    function keyFor(adapter) {
        var V = root.OrtEpVerify;
        var model = (CFG.seg && CFG.seg.model && CFG.seg.model.webgpu && CFG.seg.model.webgpu.enc) || 'edgetam';
        var parts = ['edgetam-1', model.split('/').pop().split('?')[0], (adapter && (adapter.vendor + '/' + adapter.architecture)) || 'gpu?', navigator.userAgent.slice(0, 40)];
        return V && V.verdictKey ? V.verdictKey(parts) : parts.join('|');
    }
    function onVerdict(v) {
        var V = root.OrtEpVerify;
        try {
            if (V && verdictKey && v && !v.ok && !v.error && !v.skipped && v.provider !== 'wasm') V.remember(verdictKey, v.provider, { kind: 'edgetam-kat', iou: v.iou });
        } catch (_) {}
    }

    function warm() {
        if (seg.promise) return seg.promise;
        if (!available()) { seg.state = 'off'; return Promise.resolve({ ok: false, reason: 'unavailable' }); }
        seg.state = 'loading';
        var t0 = now();
        seg.promise = ensureWorker().then(function () {
            var ob = root.__ortBuild;
            var eps = (typeof ob.wasmOnly === 'function' && ob.wasmOnly()) ? ['wasm'] : ['webgpu', 'wasm'];
            var V = root.OrtEpVerify;
            var adapterP = (V && V.adapterIdentity && eps[0] === 'webgpu') ? V.adapterIdentity() : Promise.resolve(null);
            return adapterP.then(function (adapter) {
                seg.adapter = adapter;
                verdictKey = keyFor(adapter);
                var rejected = {};
                if (V && V.isRejected) eps.forEach(function (ep) { if (ep !== 'wasm' && V.isRejected(verdictKey, ep)) rejected[ep] = 1; });
                return wcall({ type: 'segInit', ortUrl: ob.url(), ortFallback: typeof ob.fallback === 'function' ? ob.fallback() : null,
                    wasmBase: typeof ob.wasmBase === 'function' ? ob.wasmBase() : null, model: CFG.seg.model, eps: eps, rejected: rejected });
            });
        }).then(function (r) {
            seg.state = r.ok ? 'ready' : 'failed';
            seg.provider = r.provider || null;
            // A refusal is not remembered either: the next tap asks again (a
            // cold server or a dropped connection must not cost the page).
            if (!r.ok) seg.promise = null;
            return Object.assign({ ms: Math.round(now() - t0) }, r);
        }).catch(function (e) {
            seg.state = 'failed';
            // A failed load is not an answer: the next attempt asks again.
            seg.promise = null;
            return { ok: false, reason: String((e && e.message) || e).slice(0, 120) };
        });
        return seg.promise;
    }

    /* Decode the samples for the whole trimmed clip, at a low rate. Done once
       per clip and trim; a new trim invalidates the object (reset()). */
    async function sample(opts, onProgress) {
        await ensureWorker();
        var dur = Math.max(0.1, opts.trimEnd - opts.trimStart);
        var fps = Math.max(MIN_FPS, Math.min(MAX_FPS, MAX_SAMPLES / dur));
        var sc = GRID_LONG / Math.max(opts.videoW, opts.videoH);
        if (sc > 1) sc = 1;
        var gw = Math.max(2, Math.round(opts.videoW * sc / 2) * 2), gh = Math.max(2, Math.round(opts.videoH * sc / 2) * 2);
        var frames = [], transfer = [];
        var want = Math.max(1, Math.round(dur * fps));
        await root.VbrFrameSource.iterate({
            file: opts.file, url: opts.url, fps: fps,
            trimStart: opts.trimStart, trimEnd: opts.trimEnd,
            width: gw, height: gh, abortSignal: opts.abortSignal,
        }, async function (canvas, idx, tSec) {
            var d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, gw, gh).data;
            var t = (typeof tSec === 'number' && isFinite(tSec)) ? tSec : opts.trimStart + idx / fps;
            frames.push({ buf: d.buffer, t: t });
            transfer.push(d.buffer);
            if (onProgress) onProgress(Math.min(1, frames.length / want));
        });
        if (!frames.length) throw new Error('keep:no-samples');
        var times = frames.map(function (f) { return f.t; });
        await wcall({ type: 'frames', frames: frames, w: gw, h: gh, cropLane: false }, transfer);
        K = { w: gw, h: gh, fps: fps, times: times, sel: null, region: null, trimStart: opts.trimStart, trimEnd: opts.trimEnd };
        return { n: times.length, w: gw, h: gh, fps: fps };
    }

    function nearestIndex(t) {
        var best = 0, bd = Infinity;
        for (var i = 0; i < K.times.length; i++) {
            var d = Math.abs(K.times[i] - t);
            if (d < bd) { bd = d; best = i; }
        }
        return best;
    }

    /* A tap selects; further taps on the SAME sample refine the selection (the
       model gets all the points at once — it has no memory of the previous
       answer). A tap on another moment starts over there. `label` 0 excludes. */
    async function tap(nx, ny, tSec, label) {
        if (!K) return { ok: false, reason: 'no-samples' };
        var r0 = await warm();
        if (!r0.ok) return { ok: false, reason: 'model', detail: r0.reason };
        var idx = nearestIndex(tSec);
        // «Not this» only means something next to the object's own points: a
        // lone negative point on another moment asks the model for nothing.
        if (label === 0 && K.sel && K.sel.index !== idx) idx = K.sel.index;
        var p = { x: Math.max(0, Math.min(K.w - 1, nx * K.w)), y: Math.max(0, Math.min(K.h - 1, ny * K.h)), label: label === 0 ? 0 : 1 };
        var points = (K.sel && K.sel.index === idx) ? K.sel.points.concat([p]) : [p];
        var t0 = now();
        // The first tap is judged by OUTLINE ('edge', EdgeTamCore): of every
        // head and its grow-box refinement, the largest whose outline lies on
        // the frame's edges — not simply the largest (for a keep, «object plus a
        // slab of the room» is the worst answer: 0.378 of the frame for a 0.134
        // block), and not simply the sharpest (a piano's rim without the piano).
        // Every further point is a correction and wants the head the model is
        // surest of, without the grow-box pass.
        var refining = points.length > 1;
        // A correction carries the current selection's BOX with its points: the
        // points alone lose the object's scale — a «not this» on the panel
        // behind a piano lid collapsed a whole piano to its rim (1079 px of
        // 6829, 23.09.2026). The box says «this big», the points say what to
        // add or cut.
        var box = (refining && K.sel && K.sel.index === idx && K.sel.bbox) ? K.sel.bbox : null;
        var r = await wcall({ type: 'select', index: idx, points: points, box: box,
            segOpts: refining ? { mode: 'best', refine: false } : { mode: 'edge' } });
        var ok = !!(r && r.mask && r.reason === 'ok');
        if (ok) {
            K.sel = { index: idx, points: points, mask: new Uint8Array(r.mask), bbox: r.bbox, area: r.area };
            K.region = null;
        }
        return { ok: ok, reason: r ? r.reason : 'no-answer', via: r && r.via, provider: r && r.provider, ms: Math.round(now() - t0),
            cands: r && r.cands ? r.cands.map(function (c) { return [c.area, c.iou, c.edge]; }) : null,
            iou: r && r.iou, frac: r && r.area ? +(r.area / (K.w * K.h)).toFixed(3) : 0 };
    }

    async function track(onProgress) {
        if (!K || !K.sel) return { ok: false, reason: 'no-selection' };
        trackProgress = onProgress || null;
        var t0 = now();
        var maskCopy = new Uint8Array(K.sel.mask);
        var pos = K.sel.points.filter(function (p) { return p.label !== 0; });
        var res;
        try {
            res = await wcall({
                type: 'track', startIndex: K.sel.index, box: null,
                seed: pos.length ? { x: pos[0].x, y: pos[0].y } : null,
                mask: maskCopy.buffer,
                // every sample: the samples are already sparse (≤4 per second)
                segEvery: seg.provider === 'webgpu' ? 1 : 2,
            }, [maskCopy.buffer]);
        } finally { trackProgress = null; }
        var kfs = res.keyframes || [];
        if (!kfs.length || !res.masks || !res.masks.length) return { ok: false, reason: 'lost', ms: Math.round(now() - t0) };
        var kept = steadyMasks(res.masks);
        K.region = {
            id: 'keep', shape: 'rect',
            keyframes: kfs, masks: kept.masks,
            // Unlike a removal, a keep lives only where the object was SEEN:
            // holding it past a loss would paste a stale piano over whatever is
            // there now.
            startTime: res.lostBackT != null ? kfs[0].t - 1 / K.fps : -Infinity,
            endTime: res.lostFwdT != null ? kfs[kfs.length - 1].t + 1 / K.fps : Infinity,
            grow: KEEP_GROW,
            // this moment's silhouette only — see mask-builder `pairOnly`
            pairOnly: true,
            seed: seedOf(K.sel, K.times[K.sel.index]),
        };
        return { ok: true, kfs: kfs.length, masks: kept.masks.length, dropped: kept.dropped, lost: res.lost != null ? 1 : 0, mode: res.mode, ms: Math.round(now() - t0) };
    }

    /* A still object does not change size between samples; a silhouette that
       does has swallowed something (the acoustic panel behind a piano lid,
       measured) or lost a part. For a KEEP either mistake is pasted onto the
       output, so samples whose area strays from the median are dropped — the
       neighbours paint those moments instead. A removal keeps them all (its
       union wants every limb any sample saw). */
    function steadyMasks(masks) {
        var B = root.RtMaskBuilder;
        if (!B || masks.length < 3) return { masks: masks, dropped: 0 };
        var areas = masks.map(function (m) {
            var d = m.mask || B.rleDecode(m.rle, m.mw * m.mh), n = 0;
            for (var i = 0; i < d.length; i++) if (d[i]) n++;
            return (n / Math.max(1, m.mw * m.mh)) * m.w * m.h;
        });
        var sorted = areas.slice().sort(function (a, b) { return a - b; });
        var med = sorted[sorted.length >> 1];
        var out = masks.filter(function (m, i) { return areas[i] >= 0.75 * med && areas[i] <= 1.3 * med; });
        if (out.length < Math.max(2, masks.length / 3)) return { masks: masks, dropped: 0 };
        return { masks: out, dropped: masks.length - out.length };
    }

    /* The selection the person CONFIRMED with their taps, as a silhouette
       record the mask builder can transport. It is the authority on SHAPE; the
       tracker is the authority on MOTION. Measured 23.09.2026: a «not this» tap
       took the acoustic panel off a piano lid on the selected frame, and the
       tracker's own silhouettes put it back on the others (plus patches of
       stage floor by the legs) — every one of them within the area band
       steadyMasks allows. */
    var SEED_SLACK = 0.03;          // of the object's box — covers motion between samples
    function seedOf(sel, t) {
        var b = sel && sel.bbox;
        if (!b || !b.w || !b.h) return null;
        var mw = b.w, mh = b.h, crop = new Uint8Array(mw * mh);
        for (var y = 0; y < mh; y++) for (var x = 0; x < mw; x++) {
            crop[y * mw + x] = sel.mask[(b.y + y) * K.w + (b.x + x)] ? 1 : 0;
        }
        return { t: t, x: b.x / K.w, y: b.y / K.h, w: b.w / K.w, h: b.h / K.h, mw: mw, mh: mh, mask: crop };
    }
    /* Clear what the silhouette of this moment holds OUTSIDE the confirmed
       shape, carried to where the object is now (the same transport the
       tracker's silhouettes ride) and grown by SEED_SLACK. */
    function gateBySeed(core, r, b, w, h) {
        var B = root.RtMaskBuilder;
        if (!r.seed || !B.paintSilhouette || !B.dilate) return core;
        var ref = B.keyframeBoxAt(r.keyframes, r.seed.t);
        var gate = new Uint8Array(w * h);
        B.paintSilhouette(gate, w, h, r.seed, b.x * w, b.y * h, b.w * w, b.h * h, ref);
        gate = B.dilate(gate, w, h, Math.max(2, Math.round(SEED_SLACK * Math.max(b.w * w, b.h * h))));
        for (var i = 0; i < core.length; i++) if (core[i] && !gate[i]) core[i] = 0;
        return core;
    }

    function active() { return !!(K && K.region); }

    function maskAt(t, w, h) {
        if (!active() || !root.RtMaskBuilder) return null;
        var r = K.region;
        if (t < r.startTime - 1e-3 || t > r.endTime + 1e-3) return null;
        var b = root.RtMaskBuilder.keyframeBoxAt(r.keyframes, t);
        if (!b) return null;
        var ras = root.RtMaskBuilder.rasterize([{ id: r.id, shape: r.shape, x: b.x, y: b.y, w: b.w, h: b.h,
            masks: r.masks, keyframes: r.keyframes, startTime: -Infinity, endTime: Infinity, grow: r.grow, pairOnly: r.pairOnly }], t, w, h);
        return ras.bbox ? gateBySeed(ras.core, r, b, w, h) : null;
    }

    /* The edge band re-decided by local colour — the shared MaskEdge
       (public/js/mask-edge.js; moved there 21.10.2026 when object paint needed
       the same decision). Measured here first: an orange ring of stage floor
       round a piano lid at 1280 px (23.09.2026). */
    function refineEdge(core, frameData, w, h, r) { return root.MaskEdge.refineEdge(core, frameData, w, h, r); }

    /* Union the kept object into one matte, in place. Where the person's alpha
       is already higher the matte is left alone; where the object's is higher,
       alpha rises to it and the colour comes from the SOURCE frame — RVM's
       foreground estimate is meaningless on what it thought was background. */
    function mergeInto(matte, frame, t) {
        var w = matte.width, h = matte.height;
        var core = maskAt(t, w, h);
        if (!core) return false;
        var fd = frame.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, w, h).data;
        var scale = Math.max(1, Math.max(w, h) / Math.max(K.w, K.h));
        var soft = refineEdge(core, fd, w, h, Math.max(2, Math.round(scale * 2)));
        if (!soft) return false;
        var mc = matte.getContext('2d', { willReadFrequently: true });
        var md = mc.getImageData(0, 0, w, h), d = md.data;
        for (var j = 0, i = 0; j < soft.length; j++, i += 4) {
            var k = soft[j];
            if (k <= 0.02) continue;
            var a = d[i + 3] / 255;
            if (k <= a) continue;
            var f = (k - a) / k;          // share of the colour the source supplies
            d[i] = d[i] * (1 - f) + fd[i] * f;
            d[i + 1] = d[i + 1] * (1 - f) + fd[i + 1] * f;
            d[i + 2] = d[i + 2] * (1 - f) + fd[i + 2] * f;
            d[i + 3] = k * 255;
        }
        mc.putImageData(md, 0, 0);
        return true;
    }

    function previewMask() {
        return (K && K.sel) ? { mask: K.sel.mask, w: K.w, h: K.h } : null;
    }
    function hasSelection() { return !!(K && K.sel); }
    function sampled() { return !!K; }

    function reset(full) {
        if (K) { K.sel = null; K.region = null; }
        if (full) K = null;
    }

    root.VbrKeep = {
        configure: configure, available: available, warm: warm, sample: sample,
        tap: tap, track: track, active: active, maskAt: maskAt, mergeInto: mergeInto,
        previewMask: previewMask, hasSelection: hasSelection, sampled: sampled, reset: reset,
        state: function () { return seg.state; }, provider: function () { return seg.provider; },
        // pure pieces, exposed for tests/js/video-bg-remover-keep.test.mjs
        _steadyMasks: steadyMasks, _refineEdge: refineEdge,
        _install: function (k) { K = k; },     // tests: a clip state with a region
    };
})(typeof window !== 'undefined' ? window : globalThis);
